export function leadershipDialog() {
    // ========================================
    // DOM ELEMENTS & INITIALIZATION
    // ========================================
    
    const dialog = document.querySelector('.dialog--leadership');
    if (!dialog) return;

    const dialogElements = {
        close: dialog.querySelector('.dialog-close'),
        body: dialog.querySelector('.dialog-body'),
        previous: dialog.querySelector('.dialog-previous'),
        next: dialog.querySelector('.dialog-next')
    };

    const dialogTriggers = document.querySelectorAll('.leadership-dialog-trigger');
    const leadershipDetails = [...document.querySelectorAll('.leadership-detail')];
    const counterEl = dialog.querySelector('.dialog-counter');

    // ========================================
    // STATE MANAGEMENT
    // ========================================
    
    let currentIndex = null;

    // Create slug-to-index mapping for URL hash navigation
    const slugToIndexMap = new Map();
    leadershipDetails.forEach((detail, index) => {
        const slug = detail.dataset.name;
        if (slug) slugToIndexMap.set(slug, index);
    });

    // ========================================
    // UTILITY FUNCTIONS
    // ========================================
    
    const getIndexFromSlug = (slug) => slugToIndexMap.get(slug) ?? -1;

    const updateHash = (index) => {
        const slug = leadershipDetails[index]?.dataset.name;
        if (slug) {
            history.replaceState(null, null, `#${slug}`);
        }
    };

    const updateNavigationButtons = () => {
        dialogElements.previous.disabled = currentIndex === 0;
        dialogElements.next.disabled = currentIndex === leadershipDetails.length - 1;
    };

    const updateCounter = () => {
        counterEl.textContent = `${currentIndex + 1} / ${leadershipDetails.length}`;
    };

    // ========================================
    // DIALOG CONTROL FUNCTIONS
    // ========================================
    
    const handleDialogClose = () => {
        history.replaceState(null, null, window.location.pathname);
        currentIndex = null;
    };

    const closeDialog = () => {
        dialog.close();
    };

    const openDialog = (index, shouldUpdateHash = true) => {
        // Validate index bounds
        if (index < 0 || index >= leadershipDetails.length) return;

        currentIndex = index;

        // Update dialog content
        const leadershipDetail = leadershipDetails[currentIndex];
        const leadershipDetailFragment = document.createDocumentFragment();
        const clonedContent = leadershipDetail.cloneNode(true);
        
        leadershipDetailFragment.appendChild(clonedContent);
        dialogElements.body.replaceChildren(leadershipDetailFragment);

        // Reset scroll position to top
        dialogElements.body.scrollTop = 0;

        // Update UI elements
        updateNavigationButtons();
        updateCounter();

        // Update URL hash if requested
        if (shouldUpdateHash) {
            updateHash(index);
        }

        // Show dialog if not already open
        if (!dialog.open) {
            dialog.showModal();
        }
    };

    const navigateToNext = () => {
        if (currentIndex < leadershipDetails.length - 1) {
            openDialog(currentIndex + 1);
        }
    };

    const navigateToPrevious = () => {
        if (currentIndex > 0) {
            openDialog(currentIndex - 1);
        }
    };

    // ========================================
    // HASH/URL NAVIGATION
    // ========================================
    
    const checkInitialHash = () => {
        const hash = window.location.hash.slice(1);
        if (hash) {
            const index = getIndexFromSlug(hash);
            if (index !== -1) {
                openDialog(index, false);
            }
        }
    };

    const handleHashChange = () => {
        const hash = window.location.hash.slice(1);
        if (hash) {
            const index = getIndexFromSlug(hash);
            if (index !== -1 && index !== currentIndex) {
                openDialog(index, false);
            }
        } else if (dialog.open) {
            closeDialog();
        }
    };

    // ========================================
    // EVENT LISTENERS
    // ========================================
    
    // Dialog close event (handles all ways of closing: ESC, click outside, close button)
    dialog.addEventListener('close', handleDialogClose);

    // Hash change navigation
    window.addEventListener('hashchange', handleHashChange);

    // Dialog trigger buttons (open dialog)
    dialogTriggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => openDialog(index));
    });
    
    // Dialog control buttons
    dialogElements.close.addEventListener('click', closeDialog);
    dialogElements.previous.addEventListener('click', navigateToPrevious);
    dialogElements.next.addEventListener('click', navigateToNext);

    // Keyboard navigation
    dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            navigateToPrevious();
        }
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            navigateToNext();
        }
    });

    // ========================================
    // INITIALIZATION
    // ========================================
    
    checkInitialHash();
}